//
// Single source of truth for the SESSION and SCHEDULE alias grammar. Mirrors the server validator in
// blobhub-infra, where both objects reuse DEFINITION_ALIAS_SCHEMA verbatim — one handle grammar, not
// two, which is why one module serves both.
//
// NOTE: this is NOT the session-OBJECT path grammar (context/sessionObjectAlias.js) — that one allows
// upper case, dots and `/` path separators and runs 1–256 characters. Do not cross the two.
//
// The bounds reject an empty string, so this validator answers "too short" for "". An alias is
// OPTIONAL everywhere it appears: callers must skip validation for blank input and omit the key from
// the command body, rather than sending "" and asking the server to reject it.

export const SESSION_ALIAS_MIN_LENGTH = 6;
export const SESSION_ALIAS_MAX_LENGTH = 42;

// Unanchored body for an HTML5 `pattern` attribute (the browser anchors it as ^(?:…)$).
export const SESSION_ALIAS_HTML5_PATTERN = "[a-z0-9_-]*";

// Anchored form for programmatic checks, built from the same body so the two can never drift.
export const SESSION_ALIAS_PATTERN = new RegExp(`^(?:${SESSION_ALIAS_HTML5_PATTERN})$`);

// The exact shape the server refuses at create and rename — 8-4-4-4-12 lower-case hex, hyphen-separated.
// Deliberately broader than "a valid v4 UUID" (the all-zeros UUID matches too): an alias in this shape is
// indistinguishable from a generated id, so it would be unreachable by its own name and would permanently
// spend its uniqueness reservation. It sits inside SESSION_ALIAS_PATTERN's charset and length bounds, so
// it needs its own check rather than falling out of either.
export const SESSION_ALIAS_UUID_SHAPE_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export function validateSessionAlias(alias) {
  if (typeof alias !== "string") {
    return "Alias must be a string.";
  }
  if (alias.length < SESSION_ALIAS_MIN_LENGTH || alias.length > SESSION_ALIAS_MAX_LENGTH) {
    return `Alias must be ${SESSION_ALIAS_MIN_LENGTH}–${SESSION_ALIAS_MAX_LENGTH} characters ` +
      `(so "main" and "dev" are too short — try "primary" or "staging").`;
  }
  if (!SESSION_ALIAS_PATTERN.test(alias)) {
    return "Alias may contain only lower-case letters, digits, underscores and hyphens.";
  }
  if (SESSION_ALIAS_UUID_SHAPE_PATTERN.test(alias)) {
    return "Alias can't be shaped like an id (8-4-4-4-12 hex digits) — you likely pasted one by mistake, " +
      `so try a name instead, like "primary" or "staging".`;
  }
  return null;
}

export function isValidSessionAlias(alias) {
  return validateSessionAlias(alias) === null;
}
